import isAfter from 'date-fns/isAfter'
import isBefore from 'date-fns/isBefore'
import { Formik, FormikProps } from 'formik'
import { FormattedMessage, useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { InputDate } from 'components/v2/InputDate'
import { Label } from 'components/v2/Label'
import { getQueryString } from 'utils/route'
import { device } from 'utils/style'
import { ReactElement } from 'react'
import { IDepartureDateFiltersValues } from 'features/Trips/types'
import { SelectTime } from 'components/v2/SelectTime'

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
`

const DateWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const TimeWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`

const InputWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media ${device.tablet} {
    input {
      width: 104px;
    }
  }
`

interface IDepartureDateFilterFormProps extends FormikProps<IDepartureDateFiltersValues> {
  filters: TSFixMe
  getTripsQuery: TSFixMe
}

const DepartureDateFilterForm = ({
  values,
  setFieldValue,
  filters,
  getTripsQuery
}: IDepartureDateFilterFormProps): ReactElement => {
  const navigate = useNavigate()
  const intl = useIntl()

  return (
    <Wrapper>
      <DateWrapper>
        <InputWrapper>
          <Label>
            <FormattedMessage id="trips.filter.departureDate.from" defaultMessage="From" />
          </Label>
          <InputDate
            intl={intl}
            value={values.departureDateFrom}
            onChange={(value): void => {
              let departureDateTo = values.departureDateTo
              setFieldValue('departureDateFrom', value)

              if (value && isAfter(value, values.departureDateTo)) {
                departureDateTo = value
                setFieldValue('departureDateTo', value)
              }

              const query = getTripsQuery({
                departureDateFrom: value,
                departureDateTo,
                filters
              })
              navigate(`/trips?${getQueryString(query)}`)
            }}
            left
          />
        </InputWrapper>
        <InputWrapper>
          <Label>
            <FormattedMessage id="trips.filter.departureDate.to" defaultMessage="To" />
          </Label>
          <InputDate
            intl={intl}
            value={values.departureDateTo}
            onChange={(value): void => {
              let departureDateFrom = values.departureDateFrom
              setFieldValue('departureDateTo', value)
              if (value && isBefore(value, values.departureDateFrom)) {
                departureDateFrom = value
                setFieldValue('departureDateFrom', value)
              }

              const query = getTripsQuery({
                departureDateFrom,
                departureDateTo: value,
                filters
              })
              navigate(`/trips?${getQueryString(query)}`)
            }}
            right
          />
        </InputWrapper>
      </DateWrapper>
      <TimeWrapper>
        <InputWrapper>
          <Label>
            <FormattedMessage id="trips.filter.departureHour.label" defaultMessage="Departure hour" />
          </Label>
          <SelectTime
            value={filters.departureHourRange.startHour}
            onChange={(startHour): void => {
              const query = getTripsQuery({
                departureDateFrom: values.departureDateFrom,
                departureDateTo: values.departureDateTo,
                filters: {
                  ...filters,
                  departureHourRange: {
                    ...filters.departureHourRange,
                    startHour
                  }
                }
              })
              navigate(`/trips?${getQueryString(query)}`)
            }}
          />
        </InputWrapper>
        <InputWrapper>
          <SelectTime
            value={filters.departureHourRange.endHour}
            onChange={(endHour): void => {
              const query = getTripsQuery({
                departureDateFrom: values.departureDateFrom,
                departureDateTo: values.departureDateTo,
                filters: {
                  ...filters,
                  departureHourRange: {
                    ...filters.departureHourRange,
                    endHour
                  }
                }
              })
              navigate(`/trips?${getQueryString(query)}`)
            }}
            isEndOfInterval
          />
        </InputWrapper>
      </TimeWrapper>
    </Wrapper>
  )
}

interface IDepartureDateFilterProps {
  departureDateFrom: Date
  departureDateTo: Date
  filters: TSFixMe
  getTripsQuery: TSFixMe
}

export const DepartureDateFilter = ({
  departureDateFrom,
  departureDateTo,
  filters,
  getTripsQuery
}: IDepartureDateFilterProps): ReactElement => {
  const initialValues: IDepartureDateFiltersValues = {
    departureDateFrom,
    departureDateTo
  }
  return (
    <Formik
      initialValues={initialValues}
      onSubmit={(): void => {
        // Do nothing
      }}
      enableReinitialize
    >
      {(props): ReactElement => {
        return <DepartureDateFilterForm {...props} filters={filters} getTripsQuery={getTripsQuery} />
      }}
    </Formik>
  )
}
